import { CART_DRAWER_SECTION, changeCartLine } from '@theme/cart-api';
import { ThemeEvents } from '@theme/events';
import { OverlayDrawer } from '@theme/overlay-drawer';
import { parseHTML } from '@theme/section-renderer';

/**
 * @typedef {{ panel?: HTMLElement, shippingFill?: HTMLElement }} CartDrawerRefs
 */

/**
 * The cart drawer (sections/cart-drawer.liquid). Server-rendered; every cart change re-renders it
 * from the `cart-drawer` section returned by the Ajax API, and opens it when the change asks to.
 *
 * Open it with `on:click="#cart-drawer/toggle"`. Lines use `on:change="/updateLine/<key>"`
 * (quantity input) and `on:click="/removeLine/<key>"`.
 */
class CartDrawer extends OverlayDrawer {
  /** The drawer has its own scrim (`.cart-panel-scrim`), shown by `is-drawer-open` on <body> */
  usesSharedScrim = false;

  /** The free shipping bar fills from 0 the first time it is seen after a render */
  #fillPending = true;

  /** @returns {CartDrawerRefs} */
  get drawerRefs() {
    return /** @type {CartDrawerRefs} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.isConnected) return;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    super.disconnectedCallback();
  }

  /**
   * @param {HTMLElement | Event | null} [opener]
   */
  open(opener = null) {
    super.open(opener);
    if (!this.isOpen) return;
    document.body.classList.add('is-drawer-open');
    this.#animateShipping();
  }

  /**
   * @param {boolean | Event} [restoreFocus]
   */
  close(restoreFocus = true) {
    super.close(restoreFocus);
    if (!this.isOpen) document.body.classList.remove('is-drawer-open');
  }

  /** @returns {HTMLElement} */
  focusTarget() {
    return this;
  }

  /**
   * @param {string} key Line item key
   * @param {Event} event Change event from the line's quantity input
   */
  updateLine(key, event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const quantity = Math.max(parseInt(input.value, 10) || 0, 0);
    changeCartLine(key, quantity, { source: 'cart-drawer' });
  }

  /**
   * @param {string} key Line item key
   * @param {Event} event
   */
  removeLine(key, event) {
    event.preventDefault();
    changeCartLine(key, 0, { source: 'cart-drawer' });
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (!(event instanceof CustomEvent)) return;
    /** @type {import('./events.js').CartUpdateDetail} */
    const detail = event.detail;
    const html = detail.sections?.[CART_DRAWER_SECTION];
    if (html) this.#render(html);
    if (detail.openDrawer) this.open();
  };

  /** @param {string} html */
  #render(html) {
    const next = parseHTML(html).getElementById(this.id);
    if (!next) return;

    const hadFocus = this.contains(document.activeElement) && document.activeElement !== this;
    this.replaceChildren(...Array.from(next.childNodes).map((node) => document.importNode(node, true)));
    this.classList.toggle('is-empty', next.classList.contains('is-empty'));
    this.dataset.cartItemCount = next.getAttribute('data-cart-item-count') || '0';
    this.updatedCallback();

    this.#fillPending = true;
    if (this.isOpen) {
      this.#animateShipping();
      if (hadFocus) this.focus({ preventScroll: true });
    }
  }

  #animateShipping() {
    if (!this.#fillPending) return;
    const fill = this.drawerRefs.shippingFill;
    if (!fill) return;
    this.#fillPending = false;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => fill.style.setProperty('--shipping-progress', `${fill.dataset.progress || 0}%`))
    );
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
